import { useRef } from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';

const roadmapItems = [
    { id: 'wstation', name: 'Watch Station', status: 'current' },
    { id: 'wplayer', name: 'Wplayer', status: 'current' },
    { id: 'wcloudblack', name: 'Watch Cloud', status: 'current' },
    { id: 'wcopilot', name: 'W.Copilot', status: 'upcoming' },
    { id: 'wvenue', name: 'W.Venue', status: 'upcoming' },
    { id: 'wurban', name: 'W.Urban', status: 'upcoming' },
];

export const LandingRoadmap = () => {
    // Carregar todas as imagens da pasta roadmap
    const imagesGlob = import.meta.glob<{ default: string }>('@/assets/roadmap/*.{png,jpg,jpeg,svg,webp}', { eager: true });

    // Helper para encontrar a imagem correta
    const getImageUrl = (id: string) => {
        const matchingPath = Object.keys(imagesGlob).find(path => path.includes(id));
        return matchingPath ? imagesGlob[matchingPath].default : null;
    };

    return (
        <section className="py-24 lg:py-32 bg-gray-50 overflow-hidden relative">
            <div className="max-w-[1440px] mx-auto px-6">

                {/* Header */}
                <div className="text-center max-w-4xl mx-auto mb-20 animate-fade-in">
                    <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-[0.2em] mb-6">
                        <Sparkles className="w-4 h-4" />
                        Roadmap de Inovação
                    </div>

                    <h2 className="text-4xl lg:text-5xl font-black tracking-tight leading-tight mb-6 text-gray-900">
                        Construindo o futuro da<br />gestão de mídia
                    </h2>

                    <p className="text-lg lg:text-xl text-gray-500 font-medium leading-relaxed mb-8">
                        Nossa tecnologia não para. Enquanto você aproveita o máximo do
                        <span className="text-gray-900 font-bold"> Watch Station </span>
                        hoje, nossa equipe de engenharia já está preparando as próximas ferramentas para escalar sua operação.
                    </p>

                    <p className="text-sm font-bold text-green-600 uppercase tracking-widest">
                        Conheça as ferramentas que completarão nossa suíte de soluções em breve
                    </p>
                </div>

                {/* Timeline Container */}
                <div className="relative">
                    {/* Connecting Line (Absolute centered) */}
                    <div className="hidden lg:block absolute top-[50%] left-0 w-full h-1 bg-gradient-to-r from-primary/20 via-purple-500/20 to-gray-200 transform -translate-y-1/2 z-0 rounded-full" />

                    <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-8 relative z-10 overflow-x-auto lg:overflow-visible pb-12 lg:pb-0 scrollbar-hide px-4">

                        {roadmapItems.map((item, index) => {
                            const isUpcoming = item.status === 'upcoming';
                            const imageUrl = getImageUrl(item.id);

                            return (
                                <div key={item.id} className="relative group min-w-[140px] flex flex-col items-center">
                                    {/* Arrow for mobile clarity between items (optional) */}
                                    {index < roadmapItems.length - 1 && (
                                        <div className="lg:hidden absolute -bottom-8 text-gray-300 transform rotate-90">
                                            <ArrowRight />
                                        </div>
                                    )}

                                    {/* Card/Logo Container */}
                                    <div
                                        className={`
                                            w-32 h-32 lg:w-40 lg:h-40 rounded-3xl flex items-center justify-center p-6
                                            transition-all duration-500 z-20 shadow-lg group-hover:shadow-none group-hover:bg-transparent group-hover:border-transparent
                                            ${isUpcoming
                                                ? 'bg-white grayscale hover:grayscale-0 opacity-70 hover:opacity-100 border border-dashed border-gray-300'
                                                : 'bg-white border-2 border-primary/20 shadow-primary/10'
                                            }
                                        `}
                                    >
                                        {imageUrl ? (
                                            <img
                                                src={imageUrl}
                                                alt={item.name}
                                                className={`
                                                    w-full h-full object-contain object-center origin-center
                                                    transition-transform duration-500 ease-out
                                                    group-hover:scale-[2.5] relative z-50
                                                    ${isUpcoming ? 'opacity-80 group-hover:opacity-100' : ''}
                                                `}
                                            />
                                        ) : (
                                            // Fallback Placeholder if image missing
                                            <div className="text-center">
                                                <div className={`text-xs font-bold uppercase ${isUpcoming ? 'text-gray-400' : 'text-primary'}`}>
                                                    {item.id}
                                                </div>
                                                <div className="text-[10px] text-gray-300 mt-1">
                                                    Sem IMG
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Connector Dot (Visual anchor to line) */}
                                    <div className={`
                                        hidden lg:block w-4 h-4 rounded-full border-4 border-white mt-8 mb-4 shadow-sm z-10 relative
                                        ${isUpcoming ? 'bg-gray-300 group-hover:bg-purple-500' : 'bg-primary'}
                                        transition-colors duration-300
                                    `} />

                                    {/* Label */}
                                    <div className={`text-center transition-all duration-300 ${isUpcoming ? 'opacity-50 group-hover:opacity-100' : 'opacity-100'}`}>
                                        <h4 className={`font-black text-lg ${isUpcoming ? 'text-gray-500 group-hover:text-purple-600' : 'text-gray-900'}`}>
                                            {item.name}
                                        </h4>
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                                            {isUpcoming ? 'Em Breve' : 'Disponível'}
                                        </span>
                                    </div>

                                </div>
                            );
                        })}
                    </div>
                </div>

            </div>
        </section>
    );
};
